import { useCallback, useEffect, useRef, useState } from 'react';
import type {
  MemberDto,
  ServiceAnnouncementBatchSummary,
  ServiceAnnouncementPreview,
  ServiceAnnouncementTemplate,
} from '@pokerroom/types';
import { api } from '../../lib/api';
const purposeLabels: Record<string, string> = {
  HOURS_CHANGE: '營運時間異動',
  CLOSURE: '暫時休館',
  SERVICE_INTERRUPTION: '服務中斷',
  ACCESS_CHANGE: '服務流程異動',
  CORRECTION: '營運資訊更正',
};
const toIso = (value: string) => new Date(`${value}:00+08:00`).toISOString();
type Props = { members: MemberDto[]; canManage: boolean; canSend: boolean };
export function MemberNotificationsPanel({ members, canManage, canSend }: Props) {
  const [templates, setTemplates] = useState<ServiceAnnouncementTemplate[]>([]),
    [approved, setApproved] = useState<ServiceAnnouncementTemplate[]>([]);
  const [batches, setBatches] = useState<ServiceAnnouncementBatchSummary[]>([]);
  const [results, setResults] = useState<
    Record<
      string,
      { status: string; outcomes: Array<{ outcome: string; reason: string; _count: number }> }
    >
  >({});
  const [selected, setSelected] = useState<Record<string, string>>({});
  const [templateId, setTemplateId] = useState(''),
    [purpose, setPurpose] = useState('HOURS_CHANGE');
  const [startsAt, setStartsAt] = useState(''),
    [endsAt, setEndsAt] = useState(''),
    [expiresAt, setExpiresAt] = useState(''),
    [service, setService] = useState('VENUE'),
    [correctionBatchId, setCorrectionBatchId] = useState('');
  const [preview, setPreview] = useState<ServiceAnnouncementPreview | null>(null),
    [busy, setBusy] = useState(false),
    [message, setMessage] = useState('');
  const epoch = useRef(0),
    alive = useRef(true);
  const refresh = useCallback(async () => {
    const token = ++epoch.current;
    try {
      const [all, available, recent] = await Promise.all([
        canManage ? api.get<ServiceAnnouncementTemplate[]>('/member-notifications/templates') : [],
        canSend
          ? api.get<ServiceAnnouncementTemplate[]>('/member-notifications/templates/approved')
          : [],
        canManage || canSend
          ? api.get<ServiceAnnouncementBatchSummary[]>(
              canManage ? '/member-notifications/batches/manage' : '/member-notifications/batches',
            )
          : [],
      ]);
      if (token !== epoch.current) return;
      setTemplates(all);
      setApproved(available);
      setBatches(recent);
    } catch {
      if (token === epoch.current) setMessage('通知服務尚未開放或權限已變更。');
    }
  }, [canManage, canSend]);
  useEffect(() => {
    alive.current = true;
    const counter = epoch,
      mounted = alive;
    void refresh();
    return () => {
      counter.current++;
      mounted.current = false;
    };
  }, [refresh]);
  async function perform(action: () => Promise<unknown>, success: string) {
    if (busy) return;
    const token = epoch.current;
    setBusy(true);
    setMessage('');
    try {
      await action();
      if (token !== epoch.current) return;
      setMessage(success);
      await refresh();
    } catch {
      if (token === epoch.current) {
        setPreview(null);
        setMessage('無法完成；請檢查服務狀態、權限與資料，重新預覽後再試。');
      }
    } finally {
      if (alive.current) setBusy(false);
    }
  }
  const clearPreview = () => setPreview(null);
  return (
    <section
      aria-label="服務公告管理"
      className="border border-base-300 rounded-xl p-4 my-4 space-y-4"
      aria-busy={busy}
    >
      <h2 className="text-xl font-bold">服務公告</h2>
      <p>僅提供營運資訊；優惠、促購與混合內容不能以服務公告發送。通知服務預設尚未啟用。</p>
      {message && <p role="status">{message}</p>}
      {canManage && (
        <div className="space-y-2">
          <h3 className="font-bold">範本管理</h3>
          <label>
            公告用途{' '}
            <select value={purpose} onChange={(e) => setPurpose(e.target.value)} disabled={busy}>
              {Object.entries(purposeLabels).map(([key, label]) => (
                <option key={key} value={key}>
                  {label}
                </option>
              ))}
            </select>
          </label>
          <button
            className="btn btn-outline"
            disabled={busy}
            onClick={() =>
              void perform(
                () => api.post('/member-notifications/templates', { purpose, locale: 'zh-TW' }),
                '範本已建立，尚未核准。',
              )
            }
          >
            建立固定文案範本
          </button>
          <ul>
            {templates.map((template) => (
              <li key={template.id} className="flex flex-wrap gap-3 items-center py-2">
                <span>
                  {purposeLabels[template.purpose]} · {template.locale} · v{template.version}
                </span>
                {template.disabledAt ? (
                  <span>已停用</span>
                ) : (
                  <>
                    {!template.approvedAt && (
                      <button
                        className="btn btn-sm"
                        disabled={busy}
                        onClick={() =>
                          void perform(
                            () =>
                              api.post(
                                `/member-notifications/templates/${template.id}/approve`,
                                {},
                              ),
                            '範本已核准。',
                          )
                        }
                      >
                        核准範本
                      </button>
                    )}
                    <button
                      className="btn btn-sm btn-outline"
                      disabled={busy}
                      onClick={() =>
                        void perform(
                          () =>
                            api.post(`/member-notifications/templates/${template.id}/disable`, {}),
                          '範本已停用。',
                        )
                      }
                    >
                      停用
                    </button>
                  </>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}
      {canSend && (
        <>
          <h3 className="font-bold">選擇受眾與預覽</h3>
          <p>
            可從目前會員搜尋結果勾選；最多 500 人，每批須分別確認。已選{' '}
            {Object.keys(selected).length} 人。
          </p>
          <div className="grid gap-2 sm:grid-cols-2">
            {members.map((member) => (
              <label key={member.id} className="flex gap-2">
                <input
                  type="checkbox"
                  checked={Object.hasOwn(selected, member.id)}
                  disabled={
                    busy ||
                    (!Object.hasOwn(selected, member.id) && Object.keys(selected).length >= 500)
                  }
                  onChange={(e) => {
                    clearPreview();
                    setSelected((prev) => {
                      const next = { ...prev };
                      if (e.target.checked) next[member.id] = member.nickname;
                      else delete next[member.id];
                      return next;
                    });
                  }}
                />
                {member.nickname} · {member.memberNo}
              </label>
            ))}
          </div>
          <button
            className="btn btn-sm btn-outline"
            disabled={busy}
            onClick={() => {
              setSelected({});
              clearPreview();
            }}
          >
            清除受眾
          </button>
          <div className="grid gap-3 sm:grid-cols-2">
            <label>
              核准範本{' '}
              <select
                disabled={busy}
                value={templateId}
                onChange={(e) => {
                  setTemplateId(e.target.value);
                  clearPreview();
                }}
              >
                <option value="">請選擇</option>
                {approved.map((template) => (
                  <option key={template.id} value={template.id}>
                    {purposeLabels[template.purpose]} · {template.locale}
                  </option>
                ))}
              </select>
            </label>
            <label>
              服務{' '}
              <select
                disabled={busy}
                value={service}
                onChange={(e) => {
                  setService(e.target.value);
                  clearPreview();
                }}
              >
                <option value="VENUE">場館</option>
                <option value="CHECK_IN">報到服務</option>
                <option value="MEMBER_PORTAL">會員網站</option>
              </select>
            </label>
            {[
              ['開始時間', startsAt, setStartsAt],
              ['結束時間', endsAt, setEndsAt],
              ['通知到期時間', expiresAt, setExpiresAt],
            ].map(([label, value, setter]) => (
              <label key={String(label)}>
                {String(label)}（台北）
                <input
                  type="datetime-local"
                  value={value as string}
                  disabled={busy}
                  onChange={(e) => {
                    (setter as (v: string) => void)(e.target.value);
                    clearPreview();
                  }}
                />
              </label>
            ))}
            {approved.find((template) => template.id === templateId)?.purpose === 'CORRECTION' && (
              <label>
                原公告{' '}
                <select
                  value={correctionBatchId}
                  disabled={busy}
                  onChange={(e) => {
                    setCorrectionBatchId(e.target.value);
                    clearPreview();
                  }}
                >
                  <option value="">請選擇原公告</option>
                  {batches
                    .filter((batch) => batch.status !== 'PREVIEW')
                    .map((batch) => (
                      <option key={batch.id} value={batch.id}>
                        {batch.title} · {new Date(batch.createdAt).toLocaleDateString('zh-TW')}
                      </option>
                    ))}
                </select>
              </label>
            )}
          </div>
          <button
            className="btn btn-primary"
            disabled={
              busy ||
              !templateId ||
              !startsAt ||
              !endsAt ||
              !expiresAt ||
              !Object.keys(selected).length
            }
            onClick={() =>
              void perform(async () => {
                const result = await api.post<ServiceAnnouncementPreview>(
                  '/member-notifications/preview',
                  {
                    templateId,
                    memberIds: Object.keys(selected),
                    fields: {
                      startsAt: toIso(startsAt),
                      endsAt: toIso(endsAt),
                      service,
                      ...(approved.find((template) => template.id === templateId)?.purpose ===
                      'CORRECTION'
                        ? { correctionBatchId }
                        : {}),
                    },
                    expiresAt: toIso(expiresAt),
                  },
                );
                setPreview(result);
              }, '預覽已建立，尚未確認。')
            }
          >
            預覽公告
          </button>
          {preview && (
            <article className="border border-base-300 rounded-lg p-4 space-y-3">
              <h4 className="font-bold">{preview.title}</h4>
              <p className="whitespace-pre-line">{preview.body}</p>
              <p>
                類別：服務公告 · 語言：{preview.locale} · 範本版本：{preview.templateVersion}
              </p>
              <p>
                總受眾 {preview.total} 人；符合條件 {preview.eligible} 人；排除{' '}
                {preview.exclusions.length} 人。
              </p>
              {preview.exclusions.length > 0 && (
                <ul>
                  {preview.exclusions.map((item) => (
                    <li key={item.memberId}>
                      {selected[item.memberId] ?? '會員'}：
                      {item.reason === 'OPT_OUT' ? '已停用通知' : '目前不符合會員或場館資格'}
                    </li>
                  ))}
                </ul>
              )}
              <p>
                預覽有效至{' '}
                {new Date(preview.previewExpiresAt).toLocaleString('zh-TW', {
                  timeZone: 'Asia/Taipei',
                })}
                ；通知到期{' '}
                {new Date(preview.expiresAt).toLocaleString('zh-TW', { timeZone: 'Asia/Taipei' })}
              </p>
              <button
                className="btn btn-primary"
                disabled={busy || !preview.eligible}
                onClick={() =>
                  void perform(async () => {
                    await api.post(`/member-notifications/batches/${preview.id}/confirm`, {});
                    setPreview(null);
                  }, '批次已確認並記錄事件；是否已發布請查看批次結果。')
                }
              >
                確認本批通知
              </button>
            </article>
          )}
        </>
      )}
      {(canManage || canSend) && (
        <>
          <h3 className="font-bold">近期批次</h3>
          <ul>
            {batches.map((batch) => (
              <li key={batch.id} className="flex flex-wrap gap-3 items-center py-2">
                <span>
                  {batch.title} ·{' '}
                  {(
                    {
                      PREVIEW: '待確認',
                      CONFIRMED: '已確認，尚需處理',
                      PAUSED: '已暫停',
                      REVOKED: '已撤回',
                    } as Record<string, string>
                  )[batch.status] ?? batch.status}
                </span>
                {canManage && (
                  <>
                    <button
                      className="btn btn-sm"
                      disabled={busy}
                      onClick={() =>
                        void perform(async () => {
                          const result = await api.get<{
                            status: string;
                            outcomes: Array<{ outcome: string; reason: string; _count: number }>;
                          }>(`/member-notifications/batches/${batch.id}`);
                          if (alive.current)
                            setResults((prev) => ({ ...prev, [batch.id]: result }));
                        }, '批次結果已更新。')
                      }
                    >
                      查看結果
                    </button>
                    {results[batch.id] && (
                      <span>
                        已發布{' '}
                        {results[batch.id]!.outcomes.filter(
                          (item) => item.outcome === 'PUBLISHED',
                        ).reduce((total, item) => total + item._count, 0)}{' '}
                        人；已抑制{' '}
                        {results[batch.id]!.outcomes.filter(
                          (item) => item.outcome === 'SUPPRESSED',
                        ).reduce((total, item) => total + item._count, 0)}{' '}
                        人。其餘尚未完成處理。
                      </span>
                    )}
                    <button
                      className="btn btn-sm"
                      disabled={busy}
                      onClick={() =>
                        void perform(
                          () => api.post(`/member-notifications/batches/${batch.id}/pause`, {}),
                          '批次已暫停。',
                        )
                      }
                    >
                      暫停
                    </button>
                    <button
                      className="btn btn-sm btn-outline"
                      disabled={busy}
                      onClick={() =>
                        void perform(
                          () => api.post(`/member-notifications/batches/${batch.id}/revoke`, {}),
                          '批次已撤回。',
                        )
                      }
                    >
                      撤回
                    </button>
                  </>
                )}
              </li>
            ))}
          </ul>
        </>
      )}
    </section>
  );
}
